import { api } from './api';

/**
 * "Send the bill to your partner" (Aperio preset): after the booking exists,
 * the portal asks the server to email its pay link to the person named, or
 * to change that person (server: PayerPaymentController). Addressed by the
 * booking's pay token; rate-limited per IP, three changes per booking.
 *
 * Privacy: the payer's email and name go to our own server only, never to
 * analytics (the preference event carries the value `partner`, nothing more).
 */
export const PAYER_PAYMENT_ENDPOINT = 'api/payer-payment';

/** What the confirmation page can say about a refusal. */
export type PayerRequestError =
  | 'SELF'
  | 'LIMIT'
  | 'INVALID'
  | 'UNAVAILABLE'
  | 'GENERIC';

export type PayerRequestResult =
  | { ok: true; sent: boolean; name: string | null; changesLeft: number }
  | { ok: false; error: PayerRequestError };

/** The same shape of address the booking step accepts for the booker. */
export const isValidPayerEmail = (value: string): boolean => (
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim())
);

export const sameEmail = (a?: string | null, b?: string | null): boolean => (
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase()
);

interface ErrorBody {
  error?: string;
  reason?: string;
}

export function payerErrorOf(error: unknown): PayerRequestError {
  const response = (error as {
    response?: { status?: number; data?: ErrorBody };
  })?.response;
  const body = response?.data || {};
  if (body.reason === 'PAYER_IS_BOOKER') return 'SELF';
  if (body.reason === 'CHANGE_LIMIT') return 'LIMIT';
  if (body.error === 'INVALID_EMAIL' || response?.status === 422) {
    return 'INVALID';
  }
  if (body.error === 'PREPAY_UNAVAILABLE' || body.reason === 'NOT_OFFERED') {
    return 'UNAVAILABLE';
  }
  return 'GENERIC';
}

export async function requestPayerPayment(
  token: string,
  email: string,
  name?: string | null,
): Promise<PayerRequestResult> {
  try {
    const { data } = await api.post(PAYER_PAYMENT_ENDPOINT, {
      token,
      email: email.trim(),
      ...(name && name.trim() && { name: name.trim() }),
    });
    return {
      ok: true,
      sent: !!data?.sent,
      name: data?.name ?? null,
      changesLeft: Number(data?.changesLeft ?? 0),
    };
  } catch (e) {
    return { ok: false, error: payerErrorOf(e) };
  }
}
